/**
 * Shared Constants for RFQ Tracker
 *
 * Single source of truth for dropdown options and enum values
 * Used across multiple components to ensure consistency
 */

// Work Types - used in RFQ list, Job Details, and Onboarding
export const WORK_TYPES = [
    'Civil',
    'Construction',
    'CMAR',
    'Utility/Transportation',
    'Maintenance',
    'Architecture',
    'Mechanical',
    'Electrical',
    'Environmental',
    'IT/Technology',
    'Other',
    'Unknown'
];

// User Roles - used in User Management and Onboarding
export const USER_ROLES = [
    { value: 'viewer', label: 'Viewer' },
    { value: 'manager', label: 'Manager' },
    { value: 'admin', label: 'Admin' },
    { value: 'dev', label: 'Developer' }
];

// US States - used in Onboarding and potential future features
export const US_STATES = [
    'AZ', 'CA', 'CO', 'NM', 'NV', 'TX', 'UT', 'WA', 'OR', 'ID', 'MT', 'WY'
];

// ===================================================================================
// Job Status - THE single source of truth for the one Status dropdown (Job Details,
// RFQ table, City Profile, Calendar, filters). Per business-development/BD_METRICS_SPEC.md
// Section 6e ("The UI status - single source of truth"): every choice here deterministically
// writes user_status + outcome + pursuit_stage on the backend (see /update_job_status in
// rfq_scraper/api.py's STATUS_FIELD_MAP, which must be kept in sync with this list).
//
// - badgeClass: reuses the app's existing Bootstrap badge classes (no new styling system).
// - emoji: color-dot shown only in the Job Details select (table badges stay plain text,
//   matching the prior behavior).
// - outcome / pursuitStage: the BD fields this status writes. pursuitStage is null for
//   won/lost because those never regress the furthest pursuit_stage already reached.
// - requiresReason: true only for 'no_go', which requires a no_go_reason.
// ===================================================================================
export const JOB_STATUSES = [
    { value: 'new', label: 'New', emoji: '🟢', badgeClass: 'badge bg-success', outcome: 'Pending', pursuitStage: 'Identified', requiresReason: false },
    { value: 'consider', label: 'Consider', emoji: '🟠', badgeClass: 'badge bg-warning', outcome: 'Pending', pursuitStage: 'Screening', requiresReason: false },
    { value: 'pursuing', label: 'Pursuing', emoji: '🟡', badgeClass: 'badge bg-warning', outcome: 'Pending', pursuitStage: 'Go', requiresReason: false },
    { value: 'watch', label: 'Watch', emoji: '🔵', badgeClass: 'badge bg-primary', outcome: 'Pending', pursuitStage: 'Qualified', requiresReason: false },
    { value: 'no_go', label: 'No-Go', emoji: '🔴', badgeClass: 'badge bg-danger', outcome: 'No-Go', pursuitStage: 'Qualified', requiresReason: true },
    { value: 'won', label: 'Won', emoji: '✅', badgeClass: 'badge bg-success', outcome: 'Won', pursuitStage: null, requiresReason: false },
    { value: 'lost', label: 'Lost', emoji: '⚫', badgeClass: 'badge bg-dark', outcome: 'Lost', pursuitStage: null, requiresReason: false },
    { value: 'irrelevant', label: 'Irrelevant', emoji: '⚪', badgeClass: 'badge bg-secondary', outcome: 'Irrelevant', pursuitStage: 'Screening', requiresReason: false },
];

// Legacy user_status values still present on old rows. Map them to the canonical status they
// display/behave as everywhere in the UI (BD_METRICS_SPEC.md Section 6c/6e). 'completed' is
// intentionally NOT mapped here - it's ambiguous (Won or Lost?) and is handled as a special
// "Completed (legacy)" display-only state that must be manually reassigned; see normalizeStatus.
export const LEGACY_STATUS_MAP = {
    declined: 'no_go',
    ignore: 'irrelevant',
    ignored: 'irrelevant',
};

// Normalize a raw user_status (which may be a legacy value) to the canonical status it should
// display/filter/sort as. 'completed' passes through unchanged (see LEGACY_STATUS_MAP comment).
export function normalizeStatus(value) {
    if (!value) return 'new';
    return LEGACY_STATUS_MAP[value] || value;
}

// No-Go Reason Codes - used in Job Details when status = 'no_go'
// Per business-development/BD_METRICS_SPEC.md Section 3a. Do not add values here without
// updating the spec and the backend VALID_NO_GO_REASONS set in rfq_scraper/api.py.
export const NO_GO_REASONS = [
    { value: 'exp_local', label: 'No relevant local experience' },
    { value: 'exp_firmwide', label: 'No relevant firm-wide experience / qualifications' },
    { value: 'no_partner_prime', label: 'Missing a prime to sub under' },
    { value: 'no_partner_sub', label: 'Missing a subconsultant for a required discipline' },
    { value: 'no_rel_client', label: 'No relationship with this client' },
    { value: 'no_rel_contractor', label: 'No relationship with the required contractor(s)' },
    { value: 'multidisc_no_partner', label: 'Multi-discipline pursuit, no partner/sub lined up' },
    { value: 'capacity', label: 'Staff capacity - too busy to deliver' },
    { value: 'low_pwin', label: 'Low win probability (entrenched incumbent)' },
    { value: 'fee_terms', label: 'Fee / contract terms unfavorable' },
    { value: 'conflict', label: 'Conflict of interest' },
    { value: 'too_late', label: 'Found too late / deadline missed' },
    { value: 'out_of_area', label: 'Outside our service area / geography' },
    { value: 'too_small', label: 'Project too small - below our size threshold' },
    { value: 'other', label: 'Other (free text in notes)' }
];

export default {
    WORK_TYPES,
    USER_ROLES,
    US_STATES,
    JOB_STATUSES,
    LEGACY_STATUS_MAP,
    normalizeStatus,
    NO_GO_REASONS
};
